import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { formatNum, apiErr } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  CreditCard, Sparkles, Download, Check, Zap, Package, Receipt, Crown, Plus,
  ArrowLeftRight, History, XCircle, RotateCcw, AlertTriangle,
} from "lucide-react";
import { useEntitlements } from "../contexts/EntitlementsContext";
import { useAuth } from "../contexts/AuthContext";
import PlanChangeDialog from "../components/billing/PlanChangeDialog";
import ServicesCatalogue from "../components/billing/ServicesCatalogue";
import MyServices from "../components/billing/MyServices";

const fmtMoney = (n, cur = "SGD") => `${cur === "SGD" ? "S$" : cur} ${formatNum(n || 0, { decimals: 2 })}`;
const STATUS_VARIANT = { paid: "success", issued: "warning", overdue: "destructive", void: "outline" };

export default function Billing() {
  const [params, setParams] = useSearchParams();
  const { refresh: refreshEnt } = useEntitlements();
  const { user } = useAuth();
  const canBuy = user?.role === "tenant_admin" || user?.role === "super_admin";
  const [me, setMe] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [market, setMarket] = useState(null);
  const [payments, setPayments] = useState([]);
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [busy, setBusy] = useState("");
  const [planOpen, setPlanOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/billing/me").then((r) => setMe(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/billing/invoices").then((r) => setInvoices(r.data.invoices || [])).catch(() => {});
    api.get("/billing/marketplace").then((r) => setMarket(r.data)).catch(() => {});
    api.get("/billing/payments").then((r) => setPayments(r.data.payments || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const poll = useCallback(async (sid, attempt = 0, kind = "invoice") => {
    if (attempt >= 6) { toast.error("Payment status timed out. Refresh to check."); return; }
    const statusUrl = kind === "service" ? `/services/checkout/status/${sid}` : `/billing/checkout/status/${sid}`;
    try {
      const { data } = await api.get(statusUrl);
      if (data.payment_status === "paid") {
        toast.success(kind === "service" ? "Payment received — your Statement of Work is ready!"
          : kind === "plan" ? "Plan upgraded — thank you!" : "Invoice paid — thank you!");
        if (kind === "service") setTab("my-services");
        load(); refreshEnt(); return;
      }
      if (data.status === "expired") { toast.error("Payment session expired."); return; }
      setTimeout(() => poll(sid, attempt + 1, kind), 2000);
    } catch { setTimeout(() => poll(sid, attempt + 1, kind), 2000); }
  }, [load, refreshEnt]);

  useEffect(() => {
    const invSid = params.get("invoice_session");
    const planSid = params.get("plan_session");
    const svcSid = params.get("service_session");
    if (invSid || planSid || svcSid) {
      toast.info("Confirming your payment…");
      poll(svcSid || invSid || planSid, 0, svcSid ? "service" : planSid ? "plan" : "invoice");
      params.delete("invoice_session");
      params.delete("plan_session");
      params.delete("service_session");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadInvoice = async (inv) => {
    try {
      const r = await api.get(`/billing/invoices/${inv.id}.pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = u; a.download = `${inv.invoice_number}.pdf`; a.click(); URL.revokeObjectURL(u);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const payInvoice = async (inv) => {
    setBusy(inv.id);
    try {
      const { data } = await api.post(`/billing/invoices/${inv.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };

  const purchase = async (key) => {
    setBusy(key);
    try { await api.post(`/billing/addons/${key}/purchase`); toast.success("Add-on activated — it's now unlocked."); load(); refreshEnt(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const cancelAddon = async (key) => {
    setBusy(key);
    try { await api.post(`/billing/addons/${key}/cancel`); toast.success("Add-on cancelled."); load(); refreshEnt(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const setCycleFn = async (c) => {
    if (!me || c === me.billing_cycle) return;
    setBusy("cycle");
    try { await api.post("/billing/cycle", { billing_cycle: c }); toast.success(`Switched to ${c} billing.`); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const doCancel = async () => {
    setBusy("cancel");
    try { await api.post("/billing/plan/cancel"); toast.success("Cancellation scheduled for the end of your cycle."); setCancelOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const doResume = async () => {
    setBusy("resume");
    try { await api.post("/billing/plan/resume"); toast.success("Cancellation reverted — your plan continues."); load(); refreshEnt(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };
  const doReactivate = async () => {
    setBusy("reactivate");
    try {
      const { data } = await api.post("/payments/checkout/session", {
        package_code: me.tier, seats: me.subscription?.seats || 1,
        billing_cycle: me.billing_cycle || "monthly", origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };
  const cancelEff = me?.subscription?.cancel_effective_at
    ? new Date(me.subscription.cancel_effective_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })
    : null;

  const usage = me?.usage;
  const usagePct = usage && usage.free_allowance ? Math.min(100, Math.round((usage.ai_tokens / usage.free_allowance) * 100)) : 0;
  const cur = me?.currency || "SGD";

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="billing-page">
      <div>
        <div className="label-eyebrow mb-1">Account · Billing & Usage</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3">
          <CreditCard className="h-8 w-8 text-accent" /> Billing & Usage
        </h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-3xl">
          Your subscription, AI usage this month, invoices and feature add-ons. Trials are never billed — charges begin when you activate a paid plan.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-6 w-full max-w-3xl">
          <TabsTrigger value="overview" data-testid="billing-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="billing-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="billing-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="marketplace" data-testid="billing-tab-marketplace">Add-ons</TabsTrigger>
          <TabsTrigger value="services-catalogue" data-testid="billing-tab-services-catalogue">Services Catalogue</TabsTrigger>
          <TabsTrigger value="my-services" data-testid="billing-tab-my-services">My Services</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="space-y-5 mt-5">
          {!me ? <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div> : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="card-flat p-5 lg:col-span-1" data-testid="plan-card">
                  <div className="flex items-center gap-2 text-muted-foreground mb-2"><Crown className="h-4 w-4 text-accent" /><span className="label-eyebrow text-[10px]">Current plan</span></div>
                  <div className="font-display text-2xl font-bold">{me.package?.name || "No active plan"}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {me.subscription?.status === "active" ? `${me.subscription.seats} users` :
                     me.subscription?.status === "trialing" ? "Trial — not billed yet" : "—"}
                  </div>
                  <div className="font-mono text-lg mt-3">
                    {me.billing_cycle === "annual"
                      ? <>{fmtMoney(me.annual_total_sgd, cur)}<span className="text-xs text-muted-foreground">/yr</span></>
                      : <>{fmtMoney(me.recurring_monthly_sgd, cur)}<span className="text-xs text-muted-foreground">/mo</span></>}
                  </div>
                  {me.subscription?.status === "active" && (
                    <div className="mt-3">
                      <div className="inline-flex rounded-md border border-border p-0.5 text-xs" data-testid="cycle-toggle">
                        <button onClick={() => setCycleFn("monthly")} disabled={busy === "cycle"} data-testid="cycle-monthly"
                          className={`px-3 py-1 rounded-sm transition-colors ${me.billing_cycle !== "annual" ? "bg-accent text-accent-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
                        <button onClick={() => setCycleFn("annual")} disabled={busy === "cycle"} data-testid="cycle-annual"
                          className={`px-3 py-1 rounded-sm transition-colors ${me.billing_cycle === "annual" ? "bg-accent text-accent-foreground font-semibold" : "text-muted-foreground hover:text-foreground"}`}>Annual</button>
                      </div>
                      <div className="text-[11px] text-[#00E65B] mt-1.5" data-testid="annual-savings">
                        {me.billing_cycle === "annual"
                          ? `You save ${fmtMoney(me.annual_savings_sgd, cur)}/yr (${me.annual_discount_months} months free)`
                          : `Switch to annual & save ${fmtMoney(me.annual_savings_sgd, cur)}/yr (${me.annual_discount_months} months free)`}
                      </div>
                      <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setPlanOpen(true)} data-testid="change-plan-btn">
                        <ArrowLeftRight className="h-3.5 w-3.5" /> Change plan
                      </Button>
                      {me.subscription?.cancel_at_period_end ? (
                        <div className="mt-3 rounded-md border border-[#FFB800]/40 bg-[#FFB800]/10 p-3" data-testid="cancel-scheduled-notice">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-[#FFB800] shrink-0 mt-0.5" />
                            <div className="text-[11px] leading-relaxed">
                              Cancelling on <span className="font-semibold">{cancelEff || "cycle end"}</span>. Billing stops then and your workspace becomes read-only. Data is retained.
                            </div>
                          </div>
                          <Button size="sm" variant="outline" className="mt-2 w-full" disabled={busy === "resume"} onClick={doResume} data-testid="resume-plan-btn">
                            <RotateCcw className="h-3.5 w-3.5" /> Keep my plan
                          </Button>
                        </div>
                      ) : (
                        <button onClick={() => setCancelOpen(true)} data-testid="cancel-plan-btn"
                          className="mt-2 w-full text-[11px] text-muted-foreground hover:text-red-400 transition-colors py-1">
                          Cancel plan
                        </button>
                      )}
                    </div>
                  )}
                  {me.subscription?.status === "canceled" && (
                    <div className="mt-3 rounded-md border border-red-500/40 bg-red-500/10 p-3" data-testid="canceled-notice">
                      <div className="flex items-start gap-2">
                        <XCircle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
                        <div className="text-[11px] leading-relaxed">Your subscription has ended. Your workspace is read-only and your data is retained.</div>
                      </div>
                      <Button size="sm" variant="primary" className="mt-2 w-full" disabled={busy === "reactivate"} onClick={doReactivate} data-testid="reactivate-plan-btn">
                        <CreditCard className="h-3.5 w-3.5" /> Reactivate plan
                      </Button>
                    </div>
                  )}
                </div>

                <div className="card-flat p-5 lg:col-span-2" data-testid="usage-meter">
                  <div className="flex items-center gap-2 text-muted-foreground mb-3"><Zap className="h-4 w-4 text-accent" /><span className="label-eyebrow text-[10px]">AI usage · {usage?.period}</span></div>
                  <div className="flex items-end justify-between mb-1">
                    <div className="font-mono text-2xl font-bold">{formatNum(usage?.ai_tokens || 0)} <span className="text-sm text-muted-foreground">tokens</span></div>
                    <div className="text-xs text-muted-foreground">Free allowance: {formatNum(usage?.free_allowance || 0)}</div>
                  </div>
                  <Progress value={usagePct} className="h-2" />
                  <div className="grid grid-cols-3 gap-3 mt-4 text-sm">
                    <div><div className="label-eyebrow text-[9px]">AI calls</div><div className="font-mono font-bold">{formatNum(usage?.ai_calls || 0)}</div></div>
                    <div><div className="label-eyebrow text-[9px]">Billable (overage)</div><div className="font-mono font-bold">{formatNum(usage?.billable_tokens || 0)}</div></div>
                    <div><div className="label-eyebrow text-[9px]">Overage charge</div><div className="font-mono font-bold text-accent">{fmtMoney(usage?.ai_overage_sgd, cur)}</div></div>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-2">Billed at S${usage?.rate_per_1k ? (usage.rate_per_1k * 1000).toFixed(3) : "—"}/1K tokens beyond your allowance.</div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="card-flat p-4"><div className="label-eyebrow text-[10px] mb-1">Projected next invoice</div><div className="font-mono text-2xl font-bold text-accent" data-testid="projected-invoice">{fmtMoney(me.projected_next_invoice_sgd, cur)}</div><div className="text-[11px] text-muted-foreground mt-1">Subscription + AI overage + add-ons</div></div>
                <div className="card-flat p-4"><div className="label-eyebrow text-[10px] mb-1">Active add-ons</div><div className="font-mono text-2xl font-bold">{me.entitlements_summary?.addons_active || 0}</div><div className="text-[11px] text-muted-foreground mt-1">{fmtMoney(me.addon_recurring_sgd, cur)}/mo recurring</div></div>
                <div className="card-flat p-4"><div className="label-eyebrow text-[10px] mb-1">Features unlocked</div><div className="font-mono text-2xl font-bold">{me.entitlements_summary?.entitled}/{me.entitlements_summary?.total}</div><div className="text-[11px] text-muted-foreground mt-1"><button className="text-accent hover:underline" onClick={() => setTab("marketplace")}>Browse add-ons →</button></div></div>
              </div>

              {!!(me.outstanding_invoices || []).length && (
                <div className="card-flat p-4" data-testid="outstanding-banner">
                  <div className="flex items-center gap-2 mb-3"><Receipt className="h-4 w-4 text-[#FFB800]" /><span className="font-semibold text-sm">Outstanding invoices ({me.outstanding_invoices.length})</span></div>
                  {me.outstanding_invoices.map((inv) => (
                    <div key={inv.id} className="flex items-center justify-between border-t border-border/50 py-2">
                      <div className="text-sm"><span className="font-mono">{inv.invoice_number}</span> · {inv.period}</div>
                      <div className="flex items-center gap-3">
                        <span className="font-mono font-bold">{fmtMoney(inv.total, cur)}</span>
                        <Button size="sm" variant="primary" disabled={busy === inv.id} onClick={() => payInvoice(inv)} data-testid={`pay-${inv.id}`}><CreditCard className="h-3.5 w-3.5" /> {busy === inv.id ? "…" : "Pay now"}</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </TabsContent>

        {/* INVOICES */}
        <TabsContent value="invoices" className="mt-5">
          <div className="card-flat overflow-x-auto" data-testid="invoices-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Invoice</TableHead><TableHead>Period</TableHead><TableHead>Issued</TableHead>
                <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {invoices.map((inv) => (
                  <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                    <TableCell className="font-mono text-xs">{inv.invoice_number}</TableCell>
                    <TableCell className="text-xs">{inv.period}</TableCell>
                    <TableCell className="text-xs">{inv.issued_at ? new Date(inv.issued_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(inv.total, inv.currency)}</TableCell>
                    <TableCell><Badge variant={STATUS_VARIANT[inv.status] || "outline"}>{inv.status}</Badge></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => downloadInvoice(inv)} data-testid={`download-${inv.id}`}><Download className="h-3.5 w-3.5" /></Button>
                      {inv.status !== "paid" && <Button size="sm" variant="primary" disabled={busy === inv.id} onClick={() => payInvoice(inv)} data-testid={`pay-row-${inv.id}`}><CreditCard className="h-3.5 w-3.5" /> Pay</Button>}
                    </TableCell>
                  </TableRow>
                ))}
                {!invoices.length && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8 text-sm">No invoices yet. Invoices are generated each billing period.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* PAYMENTS */}
        <TabsContent value="payments" className="mt-5">
          <div className="card-flat overflow-x-auto" data-testid="payments-table">
            <div className="flex items-center gap-2 px-4 pt-4 pb-1 text-muted-foreground">
              <History className="h-4 w-4 text-accent" />
              <span className="label-eyebrow text-[10px]">Payment history</span>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Description</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {payments.map((p, i) => (
                  <TableRow key={i} data-testid={`payment-row-${i}`}>
                    <TableCell className="text-xs">{p.created_at ? new Date(p.created_at).toLocaleDateString() : "—"}</TableCell>
                    <TableCell className="text-sm">{p.description}</TableCell>
                    <TableCell className="text-right font-mono">{fmtMoney(p.amount, p.currency)}</TableCell>
                    <TableCell>
                      <Badge variant={p.payment_status === "paid" ? "success" : p.status === "expired" ? "destructive" : "warning"}>
                        {p.payment_status === "paid" ? "Paid" : p.status === "expired" ? "Expired" : "Pending"}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {!payments.length && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8 text-sm">No payments yet. Card payments made via Stripe appear here.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* MARKETPLACE */}
        <TabsContent value="marketplace" className="mt-5 space-y-5">
          {!market ? <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div> : (
            <>
              {!!market.owned.length && (
                <div>
                  <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Check className="h-3.5 w-3.5 text-[#00E65B]" /> Active add-ons</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {market.owned.map((f) => (
                      <div key={f.key} className="card-flat p-4 flex items-start justify-between gap-3" data-testid={`owned-${f.key}`}>
                        <div className="min-w-0"><div className="font-medium text-sm flex items-center gap-2"><Package className="h-4 w-4 text-accent" /> {f.name}</div><div className="text-[11px] text-muted-foreground mt-0.5">{f.description}</div></div>
                        <div className="text-right shrink-0"><div className="font-mono text-sm">{market.billing_cycle === "annual" ? <>S${f.addon_price_annual_sgd}<span className="text-[10px] text-muted-foreground">/yr</span></> : <>S${f.addon_price_sgd}<span className="text-[10px] text-muted-foreground">/mo</span></>}</div><Button size="sm" variant="outline" className="mt-1" disabled={busy === f.key} onClick={() => cancelAddon(f.key)} data-testid={`cancel-${f.key}`}>Cancel</Button></div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div>
                <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-accent" /> Available add-ons{market.tier ? ` (not in ${market.tier})` : ""}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {market.available.map((f) => (
                    <div key={f.key} className="card-flat p-4 flex items-start justify-between gap-3" data-testid={`addon-${f.key}`}>
                      <div className="min-w-0">
                        <div className="font-medium text-sm">{f.name}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">{f.description}</div>
                        <Badge variant="outline" className="text-[9px] mt-1">{f.group}</Badge>
                      </div>
                      <div className="text-right shrink-0">
                        {market.billing_cycle === "annual"
                          ? <><div className="font-mono text-base font-bold text-accent">S${f.addon_price_annual_sgd}</div><div className="text-[10px] text-muted-foreground">per entity / year</div></>
                          : <><div className="font-mono text-base font-bold text-accent">S${f.addon_price_sgd}</div><div className="text-[10px] text-muted-foreground">{f.addon_unit}</div></>}
                        <Button size="sm" variant="primary" className="mt-2" disabled={busy === f.key} onClick={() => purchase(f.key)} data-testid={`purchase-${f.key}`}><Plus className="h-3.5 w-3.5" /> {busy === f.key ? "…" : "Add"}</Button>
                      </div>
                    </div>
                  ))}
                  {!market.available.length && <div className="text-sm text-muted-foreground py-6 text-center sm:col-span-2">Your plan includes all available features. 🎉</div>}
                </div>
              </div>
            </>
          )}
        </TabsContent>

        {/* SERVICES CATALOGUE */}
        <TabsContent value="services-catalogue" className="mt-5">
          <ServicesCatalogue canBuy={canBuy} onPurchased={() => setTab("my-services")} />
        </TabsContent>

        {/* MY SERVICES */}
        <TabsContent value="my-services" className="mt-5">
          <MyServices canBuy={canBuy} />
        </TabsContent>
      </Tabs>

      <PlanChangeDialog
        open={planOpen}
        onOpenChange={setPlanOpen}
        me={me}
        onApplied={() => { load(); refreshEnt(); }}
      />

      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent className="max-w-md" data-testid="cancel-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-[#FFB800]" /> Cancel your plan?
            </DialogTitle>
            <DialogDescription className="pt-1">
              Your subscription stays active until <span className="font-semibold text-foreground">{cancelEff || "the end of your current billing cycle"}</span>. You won't be billed again.
            </DialogDescription>
          </DialogHeader>
          <ul className="text-sm space-y-2 text-muted-foreground">
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent shrink-0 mt-0.5" /> Full access continues until your cycle ends.</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent shrink-0 mt-0.5" /> After that, your workspace becomes read-only — your data is retained, not deleted.</li>
            <li className="flex gap-2"><Check className="h-4 w-4 text-accent shrink-0 mt-0.5" /> You can resume any time before the end date, or reactivate later.</li>
          </ul>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" className="flex-1" onClick={() => setCancelOpen(false)} data-testid="cancel-plan-back-btn">Keep my plan</Button>
            <Button variant="destructive" className="flex-1" disabled={busy === "cancel"} onClick={doCancel} data-testid="cancel-plan-confirm-btn">
              <XCircle className="h-4 w-4" /> Cancel at cycle end
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
